import { createContext, useContext } from "react";

import { ActionPlanData } from "src/context/Actions/useActionPlanData";
import { useGetAllActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useGetAllActionPlan";
import { useGetOneActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useGetOneActionPlan";
import { useEntityBasedScoreParams } from "src/context/EntityBasedScore/useEntityBasedScoreParams";
import { ScoreService } from "src/context/EntityBasedScore/ScoreService";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useActionPlanData } from "src/context/Actions/useActionPlanData";

type PuckActionPlanContextValue = {
    isLoading: boolean;
    isError: boolean;
    actionPlanData: ActionPlanData[];
    actionPlanStats: {
        total: number;
        engagedActions: number;
    };
    precision: number;
    setVariation: (scoreId: number, scoreVariationId: number | null) => void;
};

const PuckActionPlanContext = createContext<PuckActionPlanContextValue | null>(null);

export type HeadingScore = {
    id: string;
    label: string;
    currentScore: number | null;
    simulatedScore: number | null;
    difference: number | null;
    differencePercentage: number | null;
    unit: string;
    children?: HeadingScore[];
};

type ProviderProps = {
    children: JSX.Element;
};

export const PuckActionPlanProvider = (props: ProviderProps) => {
    const { children } = props;
    const { currentCampaign, scores = [], selectedCompany } = useFoodpilotContext();

    const scoreParams = useEntityBasedScoreParams({
        campaign: currentCampaign?.id,
        initDimensions: {
            dimensions: [["headings"]],
        },
    });

    const {
        data: actionPlans,
        isFetched: areAllActionsPlansFetched,
        isLoading: isAllActionPlansLoading,
    } = useGetAllActionPlan({
        queryParam: { "startSsqPost.company.id": selectedCompany?.id },
    });

    const selectedActionPlanID = actionPlans?.[0]?.id;

    const {
        data: actionPlan,
        isFetched: isSingleActionPlanFetched,
        isLoading: isSingleActionPlanLoading,
    } = useGetOneActionPlan({
        actionPlanId: selectedActionPlanID,
        scoreQuery: scoreParams.query,
    });

    const scoreList = ScoreService.getScoreList(
        scores,
        actionPlan?.simulationSsqPosts?.[0]?.scores || {},
        (scoreId, scoreVariationId) => {
            scoreParams.state.selectVariation(scoreId, scoreVariationId);
        },
        scoreParams.state.lastSelectedVariation,
    );

    const precision = scoreList?.lastSelected?.precision;

    const isFetched = areAllActionsPlansFetched && ((actionPlans?.length ?? 0) > 0 ? isSingleActionPlanFetched : true);

    const isLoading = (isAllActionPlansLoading || isSingleActionPlanLoading) && !isFetched;

    const actionPlanWithDefaults =
        actionPlan ?
            {
                ...actionPlan,
                startSsqPost: actionPlan.startSsqPost || { id: 0, scores: {}, elements: [] },
                actionPlanSsqPosts: actionPlan.actionPlanSsqPosts || [],
                simulationSsqPosts: actionPlan.simulationSsqPosts || [],
            }
        :   undefined;

    const actionPlanData = useActionPlanData({
        actionPlan: actionPlanWithDefaults,
        scores: scoreList.scoresWithVariations || [],
    });

    const setVariation = (scoreId: number, scoreVariationId: number | null) => {
        scoreParams.state.selectVariation(scoreId, scoreVariationId);
    };

    const actionPlanStats = {
        total: (actionPlan?.actionPlanActions ?? []).length,
        engagedActions: (actionPlan?.actionPlanSsqPosts ?? []).length,
    };

    const contextValue = {
        isLoading,
        isError: isFetched && (!actionPlanData || actionPlanData.length === 0),
        actionPlanData: actionPlanData || [],
        setVariation,
        actionPlanStats,
        precision,
    };

    if (isLoading) {
        return <></>;
    }

    return <PuckActionPlanContext.Provider value={contextValue}>{children}</PuckActionPlanContext.Provider>;
};

export const usePuckActionPlan = () => {
    const context = useContext(PuckActionPlanContext);

    if (!context) {
        return {
            isLoading: false,
            isError: false,
            actionPlanData: [],
            precision: 0,
            setVariation: () => {},
            actionPlanStats: {
                total: 0,
                engagedActions: 0,
            },
        };
    }

    return context;
};
